
import { Component, Watch, Provide, Inject, ProvideReactive } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import _ from 'lodash'

// Mixins
import TableSortMixin from '@/components/mixins/TableSortMixin.vue'
import IndoorOutdoorMixin from '@/components/mixins/IndoorOutdoorMixin.vue'

// Models
import UserModel from '@/_models/UserModel'
import BeaconModel from '@/_models/BeaconModel'
import CardModel from '@/_models/CardModel'
import { EnviromentModel } from '@/_models/EnviromentModel'

// Gmap helpers
import gmapsInit from '@/_services/gmap.service'
import IndoorOverlay from '@/_services/indoor.overlay.service'
import { MapFixedDevice } from '@/_utils/map-fixed-device'

// Helpers
import { Constant } from '@/_services/constant'
import { alertUserChangeLocation } from '@/_utils/card-alert'
import { Filter } from '@/_utils/filter'
import { isGPSMode, isCardOff } from '@/_utils/card'
import { Debounce, TimeOutPromise } from '@/_utils/utils'

// Components
import UserSearchListTable from '@/components/user/UserSearchListTable.vue'
import FloorBuildingFilterFrom from '@/components/common/indoor-outdoor/FloorBuildingFilterFrom.vue'
import DripNaviPane from './partials/DripNaviPane/index.vue'
import NaviUsersAlertModal from '@/components/indoor/partials/NaviUsersAlertModal.vue'
import CameraDisplay from '@/components/shared/CameraDisplay.vue'
import IndoorSettingButton from '@/components/common/indoor-outdoor/IndoorSettingButton.vue'
import TrackingUser from './partials/TrackingUser.vue'
import TrackingHistory from './partials/TrackingHistory.vue'

@Component({
  components: {
    UserSearchListTable,
    FloorBuildingFilterFrom,
    DripNaviPane,
    NaviUsersAlertModal,
    CameraDisplay,
    IndoorSettingButton,
  },
  computed: {},
})
export default class IndoorDisplayMixin extends mixins(TableSortMixin, IndoorOutdoorMixin) {
  filterUtilTableSort: any = new Filter('filter_indoor_table_sort')
  envInfoDisplaysStored: any = new Filter('envInfo_display_control')
  selectedMode: string = 'indoor'

  trackingMode = {
    selectedTrackingUser: new UserModel(),
    historyTracking: [],
  }

  naviAlertUsers: UserModel[] = []
  filterUtilInOutdoorTable: any = new Filter('filter_user_search_page_202011')

  @Inject('is-observer')
  isReadOnly!: () => boolean

  @ProvideReactive('indoor-env-info-display')
  envInfoDisplays = {}

  // computed
  get isLoadingUsers(): boolean {
    return this.$store.state.users.loading
  }

  get floorMapEle(): any {
    return document.getElementById('floorMap')
  }

  get tableUsersEle(): any {
    return document.getElementById('tableUsers')
  }

  get floorMapFileEle(): any {
    return document.getElementById('floorMapFile')
  }

  get centerMap(): { lat: number; lng: number } {
    return {
      lat: (this.selectedFloor.floor_sw_lat + this.selectedFloor.floor_ne_lat) / 2,
      lng: (this.selectedFloor.floor_sw_lon + this.selectedFloor.floor_ne_lon) / 2,
    }
  }

  @ProvideReactive('indoor-beacon-animation')
  get beaconAnimation(): boolean {
    return this.mapControllerConfig.beacon_animation
  }

  @ProvideReactive('indoor-env-info-display-setting')
  get envInfoDisplaySetting(): boolean {
    return this.mapControllerConfig.env_info_display
  }

  @ProvideReactive('indoor-beacon-setting-loading')
  get beaconSettingLoading(): boolean {
    return this.isSysSettingLoading
  }

  @ProvideReactive('indoor-tracking-user-name')
  get trackingUserName() {
    return this.trackingMode.selectedTrackingUser.name
  }

  @ProvideReactive('indoor-filter-env-items')
  get filteredEnvItems(): EnviromentModel[] {
    const sandIdsOnFloor = this.filterFixedDevices.map(item => item.san_id)
    return this.enviromentItems.filter(item => sandIdsOnFloor.indexOf(item.san_id) > -1)
  }

  @Provide('indoor-update-beacon-animation')
  updateBeaconAnimation() {
    this.updateSetting('indoor', 'beacon_animation')
  }

  @Provide('indoor-change-env-display-setting')
  updateEnvInfoDisplaySetting() {
    this.updateSetting('indoor', 'env_info_display')
  }

  checkAndShowAlertMessageModal(users: UserModel[]) {
    this.naviAlertUsers = users
  }

  @Watch('selectedMode')
  selectedModeChange(mode) {
    this.$router.push({ name: mode }).catch(error => false)
  }

  @Watch('users')
  async usersChanged(newList, oldList) {
    alertUserChangeLocation(this, newList, oldList)
  }

  @Watch('checkedRows')
  async selectCheckbox(users) {
    this.checkAndSetPinSelectedStatus()

    if (!this.checkedRows.length) {
      this.clearTracking()
    }
  }

  @Watch('selectedFloor')
  async displayMap() {
    if (!this.canDisplayMap || this._isDestroyed) {
      return
    }
    this.setDataSetting()

    try {
      this.removeAllMarker()

      await this.addFloorToMap()
      if (this.isFirstLoad && !this.widgetOptions) {
        if (!this.users.length) {
          await this.$store.dispatch('users/getAll', { floor: 'all' })
        }

        this.refreshUsersInFloor(this.setEnvDisplay)
      }

      if (this.google) {
        await this.displayMakers()
        this.$nextTick(() => {
          this.isFirstLoad = false
        })
      }

      ;(window as any).openCameraModal = this.openCameraModal
    } catch (error) {
      console.error(error)
    }
  }

  @Watch('selectedBuildingName')
  async selectedBuildingNameChanged(newVal, oldVal) {
    if (oldVal !== '') {
      this.searchQueryFloor = ''
    }
  }

  onEnvInfoDisplayChange() {
    this.envInfoDisplaysStored.save({
      ...this.envInfoDisplaysStored.get(),
      [this.currentSelectedFloorId]: this.envInfoDisplays,
    })
  }

  fixZoom(): void {
    if (!this.map || !this.map.getZoom()) {
      return
    }

    if (this.map.getZoom() > Constant.MAP_MAX_INIT_ZOOM) {
      this.map.setZoom(Constant.MAP_MAX_INIT_ZOOM)
    } else {
      this.map.setZoom(this.mapZoom)
    }
  }

  async addFloorToMap() {
    const el = document.getElementById(`mapIndoor${this.keyComponent}`)

    if (el === null) {
      return
    }

    this.google = await gmapsInit()
    this.map = new this.google.maps.Map(el, {
      tilt: 0,
      clickableIcons: false,
      rotateControl: false,
      streetViewControl: false,
      gestureHandling: this.$isMobileSize() ? 'greedy' : 'cooperative',
      zoomControl: !this.$isMobileSize(),
      fullscreenControl: !this.$isMobileSize(),
      center: this.centerMap,
      zoom: 20,
    })

    // Save the scaled to local store when change zoom
    this.map.addListener('zoom_changed', this.saveTheScaledOfThisBuiding)

    const overlay = IndoorOverlay(this, this.google, this.map)
    setTimeout(() => {
      if (overlay.bounds) {
        this.map.fitBounds(overlay.bounds)
        this.fixZoom()
      }
    }, 200)
  }

  gotoUserRoute(userId: string): void {
    if (this.$route.params.user_id !== userId) {
      this.$router
        .push({
          name: 'indoor',
          params: { user_id: userId },
        })
        .catch(error => false)
    }
  }

  showCameraPanel() {
    this.pinMarkers.forEach(item => {
      if (this.selectedRowHaveCamera.includes(item.fixed_id)) {
        if (item.infowindow.getMap()) {
          return
        }
        item.infowindow.close()
        item.infowindow.open(this.map, item)
      }
    })
  }

  trackingUserSelected() {
    this.trackingMode.historyTracking = []

    if (this.checkedRows.length > 1) {
      this.$buefy.modal.open({
        parent: this,
        component: TrackingUser,
        hasModalCard: true,
        props: {
          rowSelected: this.checkedRows,
        },
        events: {
          onSelected: this.openPanelTracking,
        },
      })
    } else {
      this.openPanelTracking(this.checkedRows[0])
    }
  }

  openPanelTracking(userSelected: UserModel) {
    if (!userSelected) {
      return
    }

    this.trackingMode.selectedTrackingUser = userSelected

    const deviceIdNearest = userSelected.nearest_beacon_id || userSelected.nearest_card_id

    if (deviceIdNearest) {
      this.closeAllInfowindow()

      this.pinMarkers.forEach(item => {
        if (deviceIdNearest === item.fixed_id && item.infowindow) {
          item.infowindow.open(this.map, item)
        }
      })
    }
  }

  @Provide('indoor-open-tracking-modal')
  openTrackingHistory() {
    this.$buefy.modal.open({
      parent: this,
      component: TrackingHistory,
      hasModalCard: true,
      props: {
        data: this.trackingMode.historyTracking,
        user: this.trackingMode.selectedTrackingUser,
      },
    })
  }

  clearTracking() {
    this.trackingMode.selectedTrackingUser = new UserModel()
    this.trackingMode.historyTracking = []
  }

  @Provide('indoor-toggle-env-display')
  onToggleEnvDisplay(envItem: EnviromentModel) {
    const mapId = `mapIndoor${this.keyComponent}`
    const mapElement = document.getElementById(mapId) as HTMLDivElement
    const envPanel = mapElement.querySelector(`span.map-tooltip[id="${envItem.san_id}"]`)

    if (!!this.envInfoDisplays[envItem.san_id]) {
      this.envInfoDisplays[envItem.san_id] = 0
      envPanel?.classList.add('force-hide')
    } else {
      this.envInfoDisplays[envItem.san_id] = 1
      envPanel?.classList.remove('force-hide')
    }

    this.onEnvInfoDisplayChange()
  }

  @Provide('indoor-on-mouse-enter')
  @Debounce(300)
  onMouseEnter(envItem: EnviromentModel) {
    const fixedCard = this.fixedCards.find(card => card.san_id === envItem.san_id)
    if (fixedCard) {
      this.map.panTo(new google.maps.LatLng(fixedCard.lat, fixedCard.lon))
    }
  }

  setEnvDisplay() {
    if (this.mapControllerConfig.env_info_display) {
      this.filteredEnvItems.forEach(item => {
        const isNonExist = typeof this.envInfoDisplays[item.san_id] === 'undefined'
        if (isNonExist) {
          this.envInfoDisplays[item.san_id] = 1 // set it as display by default
        }
      })
    }
  }

  getEnvConfig() {
    const currentEnvConfig = this.envInfoDisplaysStored.get({})[this.currentSelectedFloorId]
    if (!!currentEnvConfig && Object.keys(currentEnvConfig).length) {
      this.envInfoDisplays = currentEnvConfig
    }
  }

  setCenterMap(marker) {
    const center = marker.getPosition()
    this.map.setCenter(center)
  }

  usersNearestFixedDevice(fixedDevice: BeaconModel | CardModel): UserModel[] {
    const users: UserModel[] = []
    // Normal users
    if (this.users.length) {
      for (const user of this.filteredUsers) {
        const isNearest =
          user.nearest_beacon_id === fixedDevice._id || user.nearest_card_id === fixedDevice._id
        if (!isCardOff(user) && !isGPSMode(user) && isNearest) {
          users.push(user)
        }
      }
    }

    // Navi Users
    for (const naviItem of this.naviItems) {
      const isNearest = naviItem.san_id === (fixedDevice as CardModel).san_id
      if (!isNearest) {
        continue
      }
      const user = new UserModel()

      const errorMessages: string[] = []
      if (naviItem.error_message) {
        errorMessages.push(naviItem.error_message)
      }
      if (naviItem.flow === Constant.NAVI_FLOWS.NO) {
        errorMessages.push(this.$t('label.dripnavi_no') as string)
      }

      user._id = naviItem.user_id
      user.name = naviItem.user_id
      user.error_message = errorMessages.join('/')
      user.user_type = Constant.NAVI_USER_TYPE
      users.push(user)
    }

    // Enviroment Info
    for (const envItem of this.filteredEnvItems) {
      const isNearest = envItem.san_id === (fixedDevice as CardModel).san_id
      if (!isNearest) {
        continue
      }

      const userEnv = new UserModel()
      userEnv._id = envItem.group_id + '/' + envItem.san_id
      userEnv.name = envItem.san_id
      userEnv.enviroment_data = envItem.data
      userEnv.san_id = envItem.san_id
      userEnv.user_type = Constant.ENVIROMENT_USER_TYPE
      userEnv.created_at = envItem.created_at

      users.push(userEnv)
    }

    return users
  }

  async displayMakers() {
    return await this.$nextTick(async () => {
      return await TimeOutPromise(() => {
        // Attach beacons to map
        for (const fixedDevice of this.filterFixedDevices) {
          const mapFixedDevice = new MapFixedDevice(this)
          mapFixedDevice.setFixedDevice(fixedDevice)
          mapFixedDevice.setRevertAngle(this.selectedFloor.floor_angle)
          mapFixedDevice.showMarkerFixedDevice()
        }
      }, 300)
    })
  }

  closeAllInfowindow() {
    const infowindows = document.getElementsByClassName('gm-style-iw-a')
    if (infowindows && infowindows.length) {
      for (const infowindow of infowindows) {
        if (infowindow && infowindow.parentNode) {
          infowindow.parentNode.removeChild(infowindow)
        }
      }
    }
  }

  loadFloorWidgetIndoor() {
    const floorsWidgetDisplay = this.floors.find(floor => floor._id === this.widgetOptions.floor)
    if (floorsWidgetDisplay) {
      this.handleChangeFloor(floorsWidgetDisplay)
    }
  }

  async loadFloorIndoorPage() {
    this.pageFilters = this.filterUtilInOutdoorTable.get(this.pageFilters)

    await this.$store.dispatch('settings/get')
    await this.$store.dispatch('cards/getAll', { card_type: 'all' })
    await this.$store.dispatch('beacons/getAll')

    this.$store.dispatch('floors/getAll').then(async () => {
      this.checkAndChangeFloor()
    })

    if (this.manageFeature && this.manageFeature.geofence) {
      await this.$store.dispatch('geofences/getAll')
      this.refreshUsersInGeofences()
    }
  }

  // After page created
  async created() {
    this.mapMode = 'indoor'

    if (!!this.widgetOptions) {
      return this.loadFloorWidgetIndoor()
    }

    await this.loadFloorIndoorPage()
  }

  beforeDestroy() {
    // Remove all events and instance
    this.google.maps.event.clearInstanceListeners(this.map)
    this.map = null
    this._isDestroyed = true
    this.$store.dispatch('users/clearSetTimeOut')
    clearTimeout(this.refreshTimer)
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }

    // See pretty-pin file => It use for clear interval when indoor destroy
    if ((window as any).interCountEnvTime) {
      clearInterval((window as any).interCountEnvTime)
      ;(window as any).interCountEnvTime = null
    }

    this.$store.dispatch('resetAllModulesState', ['geofences'])
  }
}
